// Compliance & Data Governance (platform) — retention policies, legal holds,
// erasure/anonymization requests, retention dry-runs, audit evidence, and tenant /
// employee data-lifecycle, via compliance-service. Platform-gated server-side.
//
// SAFETY MODEL (H-COMP-1 safe slice): this is a "prove what it would do before
// doing it" framework. Destructive retention enforcement is DRY-RUN only; the only
// real anonymization that executes is compliance-service-owned records. Cross-service
// deletion/anonymization is PREVIEW-ONLY and never executed here.
//
// Endpoint status: functions marked `// REAL` hit live compliance-service endpoints.
// Functions marked `// STUB` describe a not-yet-built backend endpoint — they return
// typed empty/mock data so pages render empty-states (not error toasts). Each carries
// a TODO with the intended request. Do NOT let a STUB claim work happened.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/compliance`;

// ---------------------------------------------------------------------------
// Shared enums / literals (mirror the H-COMP-1 spec)
// ---------------------------------------------------------------------------
export type RetentionUnit = 'DAYS' | 'MONTHS' | 'YEARS';
export type TriggerEvent =
  | 'CREATED_AT'
  | 'TERMINATED_AT'
  | 'CONTRACT_ENDED_AT'
  | 'PAYROLL_RUN_LOCKED_AT'
  | 'DOCUMENT_EXPIRED_AT';
export type RetentionAction = 'REVIEW' | 'ANONYMIZE' | 'DELETE' | 'RETAIN';

export type LegalHoldSubject = 'TENANT' | 'EMPLOYEE' | 'RECORD' | 'CASE';
export type LegalHoldStatus = 'ACTIVE' | 'RELEASED';

export type ErasureType = 'EMPLOYEE_ERASURE' | 'DATA_ANONYMIZATION' | 'PRIVACY_REVIEW';
export type ErasureSource = 'EMPLOYEE' | 'HR_ADMIN' | 'PLATFORM_ADMIN' | 'LEGAL' | 'SUPPORT';
export type ErasureStatus =
  | 'REQUESTED'
  | 'REVIEWED'
  | 'APPROVED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'BLOCKED_BY_LEGAL_HOLD';

export type DryRunDecision =
  | 'DUE'
  | 'NOT_DUE'
  | 'BLOCKED_BY_LEGAL_HOLD'
  | 'RETAIN_REQUIRED'
  | 'UNSUPPORTED';

export type EvidenceAction =
  | 'RETAINED'
  | 'ANONYMIZED'
  | 'DELETED'
  | 'SKIPPED'
  | 'BLOCKED'
  | 'DRY_RUN_EVALUATED'
  | 'LEGAL_HOLD_CREATED'
  | 'LEGAL_HOLD_RELEASED';

export type TenantLifecycleStatus =
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'OFFBOARDING'
  | 'RETENTION_REVIEW'
  | 'RETENTION_LOCKED'
  | 'ANONYMIZED'
  | 'DELETED';

export type EmployeeLifecycleStatus =
  | 'ACTIVE'
  | 'INACTIVE'
  | 'TERMINATED'
  | 'ARCHIVED'
  | 'ERASURE_REQUESTED'
  | 'ANONYMIZED'
  | 'LEGAL_HOLD';

// Services in scope (for scope pickers + preview grouping).
export const GOVERNANCE_SERVICES = [
  'employee-service',
  'payroll-service',
  'leave-service',
  'attendance-service',
  'document-service',
  'compliance-service',
  'billing-service',
  'reporting-service',
] as const;
export type GovernanceService = (typeof GOVERNANCE_SERVICES)[number];

// Only compliance-service records may actually be anonymized in this safe slice.
export const EXECUTABLE_SERVICES: GovernanceService[] = ['compliance-service'];

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------
export interface DashboardSummary {
  active_legal_holds: number;
  open_erasure_requests: number;
  pending_review_requests: number;
  dry_run_findings: number;
  records_blocked_by_legal_hold: number;
  completed_anonymizations: number;
  tenants_in_offboarding: number;
  employees_pending_retention_review: number;
  recent_erasure_requests: ErasureRequest[];
  recent_legal_holds: LegalHold[];
  latest_dry_run: DryRunResult[];
  recent_audit_evidence: AuditEvidence[];
  destructive_enforcement_enabled: boolean; // must be false in this slice
}

export interface RetentionPolicy {
  id: string;
  tenant_id: string | null;
  country_code: string | null;
  service_name: GovernanceService | string;
  record_category: string;
  retention_period_value: number;
  retention_period_unit: RetentionUnit;
  trigger_event: TriggerEvent;
  action_after_retention: RetentionAction;
  legal_hold_overrides: boolean;
  is_active: boolean;
  created_by?: string | null;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
}
export type RetentionPolicyInput = Omit<
  RetentionPolicy,
  'id' | 'created_by' | 'updated_by' | 'created_at' | 'updated_at'
>;

export interface LegalHold {
  id: string;
  tenant_id: string;
  subject_type: LegalHoldSubject;
  subject_id: string;
  employee_id?: string | null;
  reason: string;
  description?: string;
  scope_services: GovernanceService[];
  status: LegalHoldStatus;
  created_by?: string | null;
  created_at: string;
  released_by?: string | null;
  released_at?: string | null;
  release_reason?: string | null;
}
export type LegalHoldInput = Pick<
  LegalHold,
  'tenant_id' | 'subject_type' | 'subject_id' | 'employee_id' | 'reason' | 'description' | 'scope_services'
>;

export interface ErasureRequest {
  id: string;
  tenant_id: string;
  employee_id: string;
  request_type: ErasureType;
  requested_by?: string | null;
  request_source: ErasureSource;
  reason: string;
  status: ErasureStatus;
  legal_hold_status: LegalHoldStatus | 'NONE';
  requested_at: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  completed_at?: string | null;
}
export type ErasureRequestInput = Pick<
  ErasureRequest,
  'tenant_id' | 'employee_id' | 'request_type' | 'request_source' | 'reason'
>;

// One line in an erasure preview / dry-run — what WOULD happen to a record group.
export interface PreviewLine {
  service_name: GovernanceService | string;
  record_category: string;
  count: number;
  outcome: 'ANONYMIZE' | 'RETAIN' | 'BLOCKED_BY_LEGAL_HOLD' | 'UNSUPPORTED';
  reason: string;
  executable: boolean; // true only for compliance-service in this slice
}
export interface ErasurePreview {
  request_id: string;
  dry_run: true;
  policy_id?: string | null;
  will_anonymize: PreviewLine[];
  will_retain: PreviewLine[];
  blocked: PreviewLine[];
  unsupported: PreviewLine[];
  generated_at: string;
}

export interface DryRunResult {
  id: string;
  run_id: string;
  tenant_id: string;
  subject_ref: string;
  service_name: GovernanceService | string;
  record_type: string;
  record_reference: string;
  policy_matched?: string | null;
  retention_trigger_date?: string | null;
  due_date?: string | null;
  proposed_action: RetentionAction;
  legal_hold_status: LegalHoldStatus | 'NONE';
  decision: DryRunDecision;
  evaluated_at: string;
}

export interface AuditEvidence {
  id: string;
  tenant_id: string;
  request_id?: string | null;
  subject_type: LegalHoldSubject;
  subject_id: string;
  employee_id?: string | null;
  service_name: GovernanceService | string;
  record_type: string;
  record_reference?: string | null;
  action: EvidenceAction;
  reason?: string;
  policy_id?: string | null;
  legal_hold_id?: string | null;
  performed_by?: string | null;
  performed_at: string;
  metadata?: Record<string, unknown>;
}

export interface TenantLifecycle {
  tenant_id: string;
  tenant_name: string;
  status: TenantLifecycleStatus;
  legal_hold_active: boolean;
  has_payroll_records: boolean;
  has_open_billing: boolean;
  active_employees: number;
  updated_at: string;
}

export interface EmployeeLifecycle {
  employee_id: string;
  tenant_id: string;
  full_name: string;
  status: EmployeeLifecycleStatus;
  employment_status: string;
  login_active: boolean;
  has_payroll_records: boolean;
  has_leave_records: boolean;
  has_documents: boolean;
  legal_hold_active: boolean;
  erasure_status?: ErasureStatus | null;
  updated_at: string;
}

// ===========================================================================
// Retention policies — REAL (compliance-service retention-policies).
// The live backend today is a simpler (data_category, retention_days, legal_hold)
// shape; we map the richer governance model onto it and keep the extra fields for
// when the backend catches up (documented in the H-COMP-1 doc).
// ===========================================================================
export const getRetentionPolicies = () =>
  axios
    .get<{ policies: any[] | null }>(`${BASE}/platform/retention-policies`)
    .then((r) => (r.data.policies ?? []).map(mapRetentionFromApi));

export const upsertRetentionPolicy = (input: RetentionPolicyInput) =>
  // REAL upsert of the (data_category, retention_days, legal_hold) core; extra
  // governance fields are sent too and ignored by the current backend.
  axios
    // Backend registers PUT (upsert) at this path — compliance-service
    // internal/privacy/handler.go:32. POST 405s.
    .put(`${BASE}/platform/retention-policies`, mapRetentionToApi(input))
    .then((r) => mapRetentionFromApi(r.data));

// PATCH is a STUB until the backend supports per-id updates; today re-upsert by
// category is the path.  TODO(H-COMP-1): PATCH /compliance/retention-policies/:id
export const disableRetentionPolicy = async (_id: string): Promise<void> => {
  // TODO(H-COMP-1): backend disable endpoint. No-op stub — never claims success DB-side.
  return;
};

function mapRetentionFromApi(p: any): RetentionPolicy {
  const days = p.retention_days ?? p.retention_period_value ?? 0;
  return {
    id: p.id,
    tenant_id: p.tenant_id ?? null,
    country_code: p.country_code ?? null,
    service_name: p.service_name ?? 'compliance-service',
    record_category: p.record_category ?? p.data_category ?? '',
    retention_period_value: p.retention_period_value ?? days,
    retention_period_unit: (p.retention_period_unit as RetentionUnit) ?? 'DAYS',
    trigger_event: (p.trigger_event as TriggerEvent) ?? 'CREATED_AT',
    action_after_retention: (p.action_after_retention as RetentionAction) ?? 'REVIEW',
    legal_hold_overrides: p.legal_hold_overrides ?? p.legal_hold ?? true,
    is_active: p.is_active ?? true,
    created_by: p.created_by ?? null,
    updated_by: p.updated_by ?? null,
    created_at: p.created_at ?? p.updated_at ?? new Date().toISOString(),
    updated_at: p.updated_at ?? new Date().toISOString(),
  };
}
function mapRetentionToApi(p: RetentionPolicyInput) {
  const days =
    p.retention_period_unit === 'YEARS'
      ? p.retention_period_value * 365
      : p.retention_period_unit === 'MONTHS'
        ? p.retention_period_value * 30
        : p.retention_period_value;
  return {
    // core (live backend):
    data_category: p.record_category,
    retention_days: days,
    legal_hold: p.legal_hold_overrides,
    // governance extras (forward-compatible; ignored by current backend):
    ...p,
  };
}

// ===========================================================================
// Erasure requests — PARTIALLY REAL. The DSAR 'delete' request + compliance-only
// fulfilment are live (privacy-requests + /fulfil dispatches to erasure). The
// richer workflow states (REVIEWED/APPROVED/…) and cross-service preview are STUBS.
// ===========================================================================
export const getErasureRequests = (status?: string): Promise<ErasureRequest[]> =>
  axios
    .get<{ requests: any[] | null }>(`${BASE}/platform/privacy-requests`, {
      params: { ...(status ? { status } : {}), type: 'delete' },
    })
    .then((r) => (r.data.requests ?? []).filter((x) => x.request_type === 'delete').map(mapErasureFromApi))
    .catch(() => []);

export const createErasureRequest = (_input: ErasureRequestInput): Promise<ErasureRequest> => {
  // TODO(H-COMP-1): dedicated erasure-request endpoint with employee_id + workflow.
  // Today the DSAR 'delete' request is created by the employee via the app; the
  // platform console reviews the queue. STUB throws so the UI shows a clear message.
  return Promise.reject(new Error('Creating erasure requests from the console is not yet supported — employees submit them in-app.'));
};

// REAL: mark reviewed/approved maps onto the DSAR status PATCH.
export const setErasureStatus = (id: string, status: string) =>
  axios
    .patch(`${BASE}/platform/privacy-requests/${id}`, { status })
    .then((r) => mapErasureFromApi(r.data));

// REAL: compliance-service-only anonymization (backend /fulfil dispatches 'delete'
// → FulfilErasure, which anonymizes ONLY compliance-owned records + emits the
// cross-service erasure event; it never touches other services).
export const executeComplianceOnlyAnonymization = (id: string) =>
  axios.post(`${BASE}/platform/privacy-requests/${id}/fulfil`).then((r) => mapErasureFromApi(r.data));

// STUB: erasure preview (what would be anonymized/retained/blocked across services).
export const getErasurePreview = (requestId: string): Promise<ErasurePreview> => {
  // TODO(H-COMP-1): POST /compliance/erasure-requests/:id/preview (dry-run collector).
  // Return a static, HONEST preview reflecting the safe slice: only compliance-service
  // is executable; everything else is preview-only/unsupported.
  return Promise.resolve({
    request_id: requestId,
    dry_run: true,
    policy_id: null,
    will_anonymize: [
      { service_name: 'compliance-service', record_category: 'privacy_request_metadata', count: 0, outcome: 'ANONYMIZE', reason: 'Compliance-owned; executable in this release', executable: true },
      { service_name: 'compliance-service', record_category: 'subject_direct_identifiers', count: 0, outcome: 'ANONYMIZE', reason: 'Compliance-owned; executable in this release', executable: true },
    ],
    will_retain: [
      { service_name: 'payroll-service', record_category: 'payroll_record', count: 0, outcome: 'RETAIN', reason: 'Retained until configured retention date (statutory)', executable: false },
      { service_name: 'payroll-service', record_category: 'payslip', count: 0, outcome: 'RETAIN', reason: 'Retained until configured retention date (statutory)', executable: false },
      { service_name: 'compliance-service', record_category: 'audit_log', count: 0, outcome: 'RETAIN', reason: 'Retained as compliance evidence', executable: false },
    ],
    blocked: [],
    unsupported: [
      { service_name: 'employee-service', record_category: 'employee_profile', count: 0, outcome: 'UNSUPPORTED', reason: 'Cross-service destructive anonymization not supported in this release', executable: false },
      { service_name: 'leave-service', record_category: 'leave_request', count: 0, outcome: 'UNSUPPORTED', reason: 'Cross-service destructive anonymization not supported in this release', executable: false },
    ],
    generated_at: new Date().toISOString(),
  });
};

function mapErasureFromApi(x: any): ErasureRequest {
  const details = x.details ?? {};
  return {
    id: x.id,
    tenant_id: x.tenant_id,
    employee_id: x.employee_id ?? details.employee_id ?? x.user_id,
    request_type: (details.request_type as ErasureType) ?? 'EMPLOYEE_ERASURE',
    requested_by: x.requested_by ?? x.user_id ?? null,
    request_source: (details.request_source as ErasureSource) ?? 'EMPLOYEE',
    reason: details.reason ?? '',
    status: mapDsarStatusToErasure(x.status),
    legal_hold_status: (details.legal_hold_status as LegalHoldStatus) ?? 'NONE',
    requested_at: x.created_at,
    reviewed_by: x.handled_by ?? null,
    reviewed_at: null,
    approved_by: null,
    approved_at: null,
    completed_at: x.status === 'completed' ? x.updated_at : null,
  };
}
function mapDsarStatusToErasure(s: string): ErasureStatus {
  const m: Record<string, ErasureStatus> = {
    pending: 'REQUESTED',
    in_review: 'REVIEWED',
    approved: 'APPROVED',
    completed: 'COMPLETED',
    rejected: 'REJECTED',
  };
  return m[s] ?? 'REQUESTED';
}

// ===========================================================================
// Retention dry-runs — PARTIALLY REAL (the worker runs server-side, dry-run).
// A per-record dry-run LISTING endpoint is a STUB.
// ===========================================================================
export const getDryRuns = (): Promise<DryRunResult[]> => {
  // TODO(H-COMP-1): GET /compliance/retention-dry-runs (per-record evaluation rows).
  // The backend retention worker currently logs a DRY-RUN summary (deletes nothing);
  // per-record rows aren't persisted yet. Return [] → page shows an informative empty state.
  return Promise.resolve([]);
};
export const runDryRun = (): Promise<{ run_id: string; note: string }> => {
  // TODO(H-COMP-1): POST /compliance/retention-dry-runs (trigger + persist rows).
  return Promise.resolve({ run_id: 'pending', note: 'Retention worker runs server-side (dry-run). On-demand runs with persisted rows are not yet available.' });
};

// ===========================================================================
// Document holds (kp-platform §10) — REAL: fans a subject-wide hold out to
// every business service's retention executor and returns per-service results.
// ===========================================================================
export interface DocumentHoldResult {
  updated: number;
  skipped: number;
  error?: string;
}
export const executeDocumentHold = (input: {
  tenant_id: string;
  user_id: string;
  action: 'apply' | 'release';
  reason?: string;
}): Promise<Record<string, DocumentHoldResult>> =>
  axios.post(`${BASE}/platform/document-holds`, input).then((r) => r.data);

// ===========================================================================
// Legal holds — STUB (no backend endpoints yet).
// ===========================================================================
export const getLegalHolds = (): Promise<LegalHold[]> => {
  // TODO(H-COMP-1): GET /compliance/legal-holds
  return Promise.resolve([]);
};
export const createLegalHold = (_input: LegalHoldInput): Promise<LegalHold> => {
  // TODO(H-COMP-1): POST /compliance/legal-holds (+ audit event)
  return Promise.reject(new Error('Legal-hold creation endpoint is not yet available (backend TODO).'));
};
export const releaseLegalHold = (_id: string, _reason: string): Promise<LegalHold> => {
  // TODO(H-COMP-1): POST /compliance/legal-holds/:id/release (+ audit event)
  return Promise.reject(new Error('Legal-hold release endpoint is not yet available (backend TODO).'));
};

// ===========================================================================
// Audit evidence — STUB (falls back to the platform audit log where possible).
// ===========================================================================
export const getAuditEvidence = (): Promise<AuditEvidence[]> => {
  // TODO(H-COMP-1): GET /compliance/audit-evidence (compliance-scoped evidence rows).
  return Promise.resolve([]);
};

// ===========================================================================
// Tenant / employee lifecycle — STUB (read from tenant/employee services later).
// ===========================================================================
export const getTenantLifecycles = (): Promise<TenantLifecycle[]> => {
  // TODO(H-COMP-1): GET /compliance/tenant-lifecycle
  return Promise.resolve([]);
};
export const getEmployeeLifecycles = (): Promise<EmployeeLifecycle[]> => {
  // TODO(H-COMP-1): GET /compliance/employee-lifecycle
  return Promise.resolve([]);
};

// ===========================================================================
// Dashboard — assembled from the real sources we have; unavailable counters are 0.
// ===========================================================================
export const getDashboard = async (): Promise<DashboardSummary> => {
  const [erasures] = await Promise.all([getErasureRequests().catch(() => [] as ErasureRequest[])]);
  const open = erasures.filter((e) => !['COMPLETED', 'REJECTED', 'CANCELLED'].includes(e.status));
  return {
    active_legal_holds: 0,
    open_erasure_requests: open.length,
    pending_review_requests: erasures.filter((e) => e.status === 'REQUESTED').length,
    dry_run_findings: 0,
    records_blocked_by_legal_hold: 0,
    completed_anonymizations: erasures.filter((e) => e.status === 'COMPLETED').length,
    tenants_in_offboarding: 0,
    employees_pending_retention_review: 0,
    recent_erasure_requests: erasures.slice(0, 8),
    recent_legal_holds: [],
    latest_dry_run: [],
    recent_audit_evidence: [],
    destructive_enforcement_enabled: false,
  };
};
